import { render } from 'preact';
import { useState, useEffect } from 'preact/hooks';

const GRAPHQL_QUERY = `
  query($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        title
        handle
        description
        variants(first: 1) {
          edges {
            node {
              id
              title
            }
          }
        }
        tags
        productType
        collections(first: 25) {
          edges {
            node {
              handle
            }
          }
        }
        subbrand_prefix: metafield(namespace: "cirkul", key: "subbrand_prefix") {
          value
        }
        isOutOfStock: metafield(namespace: "cirkul", key: "out_of_stock") {
          value
        }
        launchTimestamp: metafield(namespace: "cirkul", key: "launch_timestamp") {
          value
          type
        }
        images(first: 1) {
          edges {
            node {
              src
            }
          }
        }
        flavorProfiles: metafield(namespace: "cirkul", key: "sip_modal") {
          reference {
            ... on Metaobject {
              flavor_profile_strings: field(key: "flavor_profile_strings") {
                value
              }
              flavor_profile_icons: field(key: "flavor_profile_icons") {
                references(first: 10) {
                  nodes {
                    ... on Metaobject {
                      id
                      icon: field(key: "image") {
                        reference {
                          ... on MediaImage {
                            image {
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        modalContent: metafield(namespace: "cirkul", key: "sip_modal") {
          reference {
            ... on Metaobject {
              firstImage: field(key: "modal_images") {
                firstRef: references(first: 3) {
                  nodes {
                    ... on MediaImage {
                      image {
                        url
                        altText
                      }
                    }
                  }
                }
              }
              nutritionImage: field(key: "nutrition_image") {
                reference {
                  ... on MediaImage {
                    image {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
        }
        newUntilTimestamp: metafield(namespace: "cirkul", key: "new_until_timestamp") {
          value
          type
        }
      }
    }
  }
`;

export const PRODUCT_GRAPHQL_FRAGMENT = `
  fragment ProductFields on Product {
    id
    title
    handle
    description
    variants(first: 1) {
      edges {
        node {
          id
          title
        }
      }
    }
    tags
    productType
    subbrand_prefix: metafield(namespace: "cirkul", key: "subbrand_prefix") {
      value
    }
    isOutOfStock: metafield(namespace: "cirkul", key: "out_of_stock") {
      value
    }
    launchTimestamp: metafield(namespace: "cirkul", key: "launch_timestamp") {
      value
      type
    }
    images(first: 1) {
      edges {
        node {
          src
        }
      }
    }
    flavorProfiles: metafield(namespace: "cirkul", key: "sip_modal") {
      reference {
        ... on Metaobject {
          flavor_profile_strings: field(key: "flavor_profile_strings") {
            value
          }
          flavor_profile_icons: field(key: "flavor_profile_icons") {
            references(first: 10) {
              nodes {
                ... on Metaobject {
                  id
                  icon: field(key: "image") {
                    reference {
                      ... on MediaImage {
                        image {
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    modalContent: metafield(namespace: "cirkul", key: "sip_modal") {
      reference {
        ... on Metaobject {
          firstImage: field(key: "modal_images") {
            firstRef: references(first: 3) {
              nodes {
                ... on MediaImage {
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
          nutritionImage: field(key: "nutrition_image") {
            reference {
              ... on MediaImage {
                image {
                  url
                  altText
                }
              }
            }
          }
        }
      }
    }
    newUntilTimestamp: metafield(namespace: "cirkul", key: "new_until_timestamp") {
      value
      type
    }
  }
`;

export async function fetchProducts(ids) {
  const response = await fetch('/api/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': '722b17daa2fefc4412d3f554625ded96',
    },
    body: JSON.stringify({
      query: GRAPHQL_QUERY,
      variables: { ids },
    }),
  });
  const data = await response.json();
  
  // Process the nutrition image safely
  const products = data.data.nodes.map((node) => ({
    ...node,
    subbrand_prefix: node.subbrand_prefix?.value || '',
    launch_timestamp: node.launchTimestamp?.value || 0,
    isOutOfStock: node.isOutOfStock?.value === "true",
    nutritionImageUrl: node.modalImage?.reference?.nutritionImage?.reference?.image?.url || null,
    nutritionImageAlt: node.modalImage?.reference?.nutritionImage?.reference?.image?.altText || '',
  }));
  
  return products;
}

export function ProductQL({ classes, placeholder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.length >= 2) {
      setLoading(true);
      fetchProducts([`gid://shopify/Product/${query}`])
        .then((products) => {
          setResults(products);
          setLoading(false);
        })
        .catch(() => {
          setResults([]);
          setLoading(false);
        });
    } else {
      setResults([]);
    }
  }, [query]);

  return (
    <div className={`${classes}`}>
      <input
        type="text"
        className="tw-mb-4 tw-w-full tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-4"
        placeholder={placeholder || 'Search for products...'}
        value={query}
        onInput={(e) => setQuery(e.target.value)}
      />
      {loading && <div className="tw-text-gray-500">Loading...</div>}
      {results.length > 0 && (
        <ul>
          {results.map((product) => (
            <li key={product.id}>
              {product.title}
              {product.nutritionImageUrl && (
                <img 
                  src={product.nutritionImageUrl} 
                  alt={product.nutritionImageAlt}
                  className="tw-mt-2 tw-max-w-full"
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export async function Root(element, data) {
  const { classes, placeholder } = data;
  render(<ProductQL classes={classes} placeholder={placeholder} />, element);
}

const COLLECTION_PRODUCTS_QUERY = `
  query($handle: String!, $first: Int!, $after: String, $sortKey: ProductCollectionSortKeys) {
    collectionByHandle(handle: $handle) {
      id
      title
      handle
      description
      products(first: $first, after: $after, sortKey: $sortKey) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            title
            handle
            description
            descriptionHtml
            tags
            productType
            price: priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPrice: compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            available: availableForSale
            createdAt
            vendor
            collections(first: 10) {
              edges {
                node {
                  title
                }
              }
            }
            featuredImage {
              url(transform: {maxWidth: 800})
              altText
            }
            images(first: 5) {
              edges {
                node {
                  url(transform: {maxWidth: 800})
                  altText
                }
              }
            }
            variants(first: 250) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  image {
                    url(transform: {maxWidth: 800})
                    altText
                  }
                  launch_timestamp: metafield(namespace: "cirkul", key: "launch_timestamp") {
                    value
                    type
                  }
                  grid_image: metafield(namespace: "cirkul", key: "2025_grid_image") {
                    reference {
                      ... on MediaImage {
                        image {
                          url(transform: {maxWidth: 800})
                        }
                      }
                    }
                  }
                  variant_filtering_tags: metafield(namespace: "cirkul", key: "variant_filtering_tags") {
                    references(first: 10) {
                      nodes {
                        ... on Metaobject {
                          id
                          internal_name: field(key: "internal_name") {
                            value
                          }
                          category: field(key: "category") {
                            reference {
                              ... on Metaobject {
                                id
                                internal_name: field(key: "internal_name") {
                                  value
                                }
                                label: field(key: "label") {
                                  value
                                }
                              }
                            }
                          }
                          label: field(key: "label") {
                            value
                          }
                        }
                      }
                    }
                  }
                  sip_modal: metafield(namespace: "cirkul", key: "sip_modal") {
                    reference {
                      ... on Metaobject {
                        id
                        flavor_profile_strings: field(key: "flavor_profile_strings") {
                          value
                        }
                        flavor_profile_icons: field(key: "flavor_profile_icons") {
                          references(first: 10) {
                            nodes {
                              ... on Metaobject {
                                id
                                icon: field(key: "image") {
                                  reference {
                                    ... on MediaImage {
                                      image {
                                        url(transform: {maxWidth: 400})
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                        modal_images: field(key: "modal_images") {
                          references(first: 5) {
                            nodes {
                              ... on MediaImage {
                                image {
                                  url(transform: {maxWidth: 800})
                                  altText
                                }
                              }
                            }
                          }
                        }
                        nutrition_image: field(key: "nutrition_image") {
                          reference {
                            ... on MediaImage {
                              image {
                                url(transform: {maxWidth: 800})
                                altText
                              }
                            }
                          }
                        }
                        flavor_line_collection: field(key: "flavor_line_collection") {
                          reference {
                            ... on Collection {
                              title
                              handle
                            }
                          }
                        }
                        override_sip_modal_background_mobile: field(key: "override_sip_modal_background_mobile") {
                          reference {
                            ... on MediaImage {
                              image {
                                url(transform: {maxWidth: 800})
                              }
                            }
                          }
                        }
                        override_sip_modal_background_desktop: field(key: "override_sip_modal_background_desktop") {
                          reference {
                            ... on MediaImage {
                              image {
                                url(transform: {maxWidth: 1200})
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            subtitle: metafield(namespace: "descriptors", key: "subtitle") {
              value
            }
            subbrand_prefix: metafield(namespace: "cirkul", key: "subbrand_prefix") {
              value
            }
            isOutOfStock: metafield(namespace: "cirkul", key: "out_of_stock") {
              value
            }
            launchTimestamp: metafield(namespace: "cirkul", key: "launch_timestamp") {
              value
              type
            }
            newUntilTimestamp: metafield(namespace: "cirkul", key: "new_until_timestamp") {
              value
              type
            }
            hideFromSite: metafield(namespace: "cirkul", key: "hide_from_site") {
              value
            }
            starterKit: metafield(namespace: "cirkul", key: "starter_kit") {
              value
            }
            limitPerOrder: metafield(namespace: "cirkul", key: "limit_per_order") {
              value
            }
            
            # NEW FILTERING METAFIELDS
            product_filtering_class: metafield(namespace: "cirkul", key: "product_filtering_class") {
              reference {
                ... on Metaobject {
                  id
                  internal_name: field(key: "internal_name") {
                    value
                  }
                  label: field(key: "label") {
                    value
                  }
                }
              }
            }
            product_filtering_tags: metafield(namespace: "cirkul", key: "product_filtering_tags") {
              references(first: 20) {
                nodes {
                  ... on Metaobject {
                    id
                    internal_name: field(key: "internal_name") {
                      value
                    }
                    category: field(key: "category") {
                      reference {
                        ... on Metaobject {
                          id
                          internal_name: field(key: "internal_name") {
                            value
                          }
                          label: field(key: "label") {
                            value
                          }
                        }
                      }
                    }
                    label: field(key: "label") {
                      value
                    }
                  }
                }
              }
            }
            
            grid_image: metafield(namespace: "cirkul", key: "2025_grid_image") {
              reference {
                ... on MediaImage {
                  image {
                    url(transform: {maxWidth: 800})
                  }
                }
              }
            }
            bundle_builder_img: metafield(namespace: "cirkul", key: "bundle_builder_img") {
              reference {
                ... on MediaImage {
                  image {
                    url(transform: {maxWidth: 800})
                  }
                }
              }
            }
            sip_modal: metafield(namespace: "cirkul", key: "sip_modal") {
              reference {
                ... on Metaobject {
                  id
                  display_name_admin_only: field(key: "display_name_admin_only") {
                    value
                  }
                  title: field(key: "title") {
                    value
                  }
                  is_flvr_lab: field(key: "is_flvr_lab") {
                    value
                  }
                  flavor_profile_strings: field(key: "flavor_profile_strings") {
                    value
                  }
                  flavor_profile_icons: field(key: "flavor_profile_icons") {
                    references(first: 10) {
                      nodes {
                        ... on Metaobject {
                          id
                          icon: field(key: "image") {
                            reference {
                              ... on MediaImage {
                                image {
                                  url(transform: {maxWidth: 400})
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  modal_images: field(key: "modal_images") {
                    references(first: 5) {
                      nodes {
                        ... on MediaImage {
                          image {
                            url(transform: {maxWidth: 800})
                            altText
                          }
                        }
                      }
                    }
                  }
                  nutrition_image: field(key: "nutrition_image") {
                    reference {
                      ... on MediaImage {
                        image {
                          url(transform: {maxWidth: 800})
                          altText
                        }
                      }
                    }
                  }
                  flavor_line_collection: field(key: "flavor_line_collection") {
                    reference {
                      ... on Collection {
                        title
                        handle
                      }
                    }
                  }
                  override_sip_modal_background_mobile: field(key: "override_sip_modal_background_mobile") {
                    reference {
                      ... on MediaImage {
                        image {
                          url(transform: {maxWidth: 800})
                        }
                      }
                    }
                  }
                  override_sip_modal_background_desktop: field(key: "override_sip_modal_background_desktop") {
                    reference {
                      ... on MediaImage {
                        image {
                          url(transform: {maxWidth: 1200})
                        }
                      }
                    }
                  }
                }
              }
            }
            bundleComponents: metafield(namespace: "cirkul", key: "bundle_components") {
              reference {
                ... on Metaobject {
                  id
                  fields {
                    key
                    value
                  }
                }
              }
            }
            customizableGoodsData: metafield(namespace: "custom", key: "product_modal_group") {
              reference {
                ... on Metaobject {
                  id
                  active_in_production: field(key: "active_in_production") {
                    value
                  }
                  grid_card_title: field(key: "grid_card_title") {
                    value
                  }
                  grid_card_image: field(key: "grid_card_image") {
                    reference {
                      ... on MediaImage {
                        image {
                          url(transform: {maxWidth: 800})
                          altText
                        }
                      }
                    }
                  }
                  dropdown_label: field(key: "dropdown_label") {
                    value
                  }
                  default_member: field(key: "default_member") {
                    reference {
                      ... on Product {
                        id
                        title
                        handle
                        description
                        descriptionHtml
                        price: priceRange {
                          minVariantPrice {
                            amount
                            currencyCode
                          }
                        }
                        variants(first: 10) {
                          edges {
                            node {
                              id
                              title
                              availableForSale
                              price {
                                amount
                                currencyCode
                              }
                              selectedOptions {
                                name
                                value
                              }
                              image {
                                url(transform: {maxWidth: 800})
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  members: field(key: "members") {
                    references(first: 20) {
                      nodes {
                        ... on Metaobject {
                          id
                          dropdown_option_label: field(key: "dropdown_option_label") {
                            value
                          }
                          product: field(key: "product") {
                            reference {
                              ... on Product {
                                id
                                title
                                handle
                                description
                                descriptionHtml
                                price: priceRange {
                                  minVariantPrice {
                                    amount
                                    currencyCode
                                  }
                                }
                                variants(first: 10) {
                                  edges {
                                    node {
                                      id
                                      title
                                      availableForSale
                                      price {
                                        amount
                                        currencyCode
                                      }
                                      selectedOptions {
                                        name
                                        value
                                      }
                                      image {
                                        url(transform: {maxWidth: 800})
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Cache for collection products - stores by collection handle
const productCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache duration

// Helper to check if cache is valid
function isCacheValid(cacheEntry) {
    return cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_DURATION;
}


export async function fetchCollectionProducts(handle, limit = 250, cursor = null, sortKey = 'COLLECTION_DEFAULT') {
    const STOREFRONT_ACCESS_TOKEN = window?.SHOPIFY_STOREFRONT_ACCESS_TOKEN || '722b17daa2fefc4412d3f554625ded96';
    
    // Create cache key - include sortKey since different sorts return different orders
    const cacheKey = `${handle}_${sortKey}_${limit}`;
    
    // Check if we have valid cached data
    const cachedData = productCache.get(cacheKey);
    if (isCacheValid(cachedData)) {
        return cachedData.products;
    }
    
    let allProducts = [];
    let hasNextPage = true;
    let currentCursor = cursor;
    let requestCount = 0;
    const maxRequests = 5;
        
    while (hasNextPage && requestCount < maxRequests && allProducts.length < limit) {
        try {            
            const requestBody = {
                query: COLLECTION_PRODUCTS_QUERY,
                variables: { 
                  handle, 
                  first: Math.min(limit - allProducts.length, 250),
                  after: currentCursor,
                  sortKey: sortKey 
                },
              };
              
              const response = await fetch('/api/graphql', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
                },
                body: JSON.stringify(requestBody),
              });

              if (!response.ok) {
                const responseText = await response.text();
                throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
              }

              const data = await response.json();
              
              if (data.errors) {
                throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
              }

              const collection = data.data?.collectionByHandle;
              if (!collection) {
                throw new Error(`Collection not found: ${handle}`);
              }

              const products = collection.products.edges.map(({ node }) => processProduct(node));
              allProducts = allProducts.concat(products);
              
              hasNextPage = collection.products.pageInfo.hasNextPage && allProducts.length < limit;
              currentCursor = collection.products.pageInfo.endCursor;
              requestCount++;
              
              // Log first few product titles to verify sort order (only on first batch)
              if (requestCount === 1) {
                // Products fetched with correct sort order
              }
              
              if (hasNextPage && requestCount < maxRequests) {
                await new Promise(resolve => setTimeout(resolve, 100));
              }
              
            } catch (error) {
              break;
            }
        }

    // Cache the results
    if (allProducts.length > 0) {
        productCache.set(cacheKey, {
            products: allProducts,
            timestamp: Date.now()
        });
    }

    return allProducts;
}

// Optional: Add function to clear cache if needed
export function clearProductCache(handle = null) {
    if (handle) {
        // Clear cache for specific collection
        const keysToDelete = Array.from(productCache.keys()).filter(key => key.startsWith(handle));
        keysToDelete.forEach(key => productCache.delete(key));
    } else {
        // Clear all cache
        productCache.clear();
    }
}

// NEW: Separate query for customizable goods details (fetch on modal open)
const CUSTOMIZABLE_GOODS_QUERY = `
  query($productId: ID!) {
    product(id: $productId) {
      id
      customizableGoodsData: metafield(namespace: "custom", key: "product_modal_group") {
        reference {
          ... on Metaobject {
            id
            active_in_production: field(key: "active_in_production") {
              value
            }
            dropdown_label: field(key: "dropdown_label") {
              value
            }
            default_member: field(key: "default_member") {
              reference {
                ... on Product {
                  id
                  title
                  handle
                  description
                  descriptionHtml
                  price: priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  variants(first: 10) {
                    edges {
                      node {
                        id
                        title
                        availableForSale
                        price {
                          amount
                          currencyCode
                        }
                        selectedOptions {
                          name
                          value
                        }
                        image {
                          url(transform: {maxWidth: 800})
                        }
                      }
                    }
                  }
                }
              }
            }
            members: field(key: "members") {
              references(first: 20) {
                nodes {
                  ... on Metaobject {
                    id
                    dropdown_option_label: field(key: "dropdown_option_label") {
                      value
                    }
                    product: field(key: "product") {
                      reference {
                        ... on Product {
                          id
                          title
                          handle
                          description
                          descriptionHtml
                          price: priceRange {
                            minVariantPrice {
                              amount
                              currencyCode
                            }
                          }
                          variants(first: 10) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                price {
                                  amount
                                  currencyCode
                                }
                                selectedOptions {
                                  name
                                  value
                                }
                                image {
                                  url(transform: {maxWidth: 800})
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// NEW: Function to fetch customizable goods details
export async function fetchCustomizableGoodsDetails(productId) {
    const STOREFRONT_ACCESS_TOKEN = window?.SHOPIFY_STOREFRONT_ACCESS_TOKEN || '722b17daa2fefc4412d3f554625ded96';
    
    try {
        const response = await fetch('/api/graphql', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
            },
            body: JSON.stringify({
                query: CUSTOMIZABLE_GOODS_QUERY,
                variables: { productId: `gid://shopify/Product/${productId}` }
            }),
        });

        if (!response.ok) {
            throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data.errors) {
            console.error('❌ GraphQL errors:', data.errors);
            throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        const product = data.data?.product;
        if (!product?.customizableGoodsData?.reference) {
            return null;
        }

        // Process the customizable goods data
        const reference = product.customizableGoodsData.reference;
        return {
            dropdown_label: reference.dropdown_label?.value || 'Size',
            default_member: reference.default_member?.reference ? {
                dropdown_option_label: 'Default',
                product: processCustomizableProduct(reference.default_member.reference)
            } : null,
            members: reference.members?.references?.nodes?.map(member => ({
                id: member.id,
                dropdown_option_label: member.dropdown_option_label?.value || '',
                product: member.product?.reference ? processCustomizableProduct(member.product.reference) : null
            })).filter(member => member.product) || []
        };
    } catch (error) {
        console.error('❌ Error fetching customizable goods details:', error);
        return null;
    }
}

// Helper function to process customizable goods products (moved outside main function)
function processCustomizableProduct(productNode) {
    if (!productNode || Object.keys(productNode).length === 0) return null;
    
    const formatPrice = (priceObj) => {
        if (!priceObj?.amount) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: priceObj.currencyCode || 'USD'
        }).format(parseFloat(priceObj.amount));
    };
    
    return {
        id: parseInt(productNode.id.replace('gid://shopify/Product/', '')),
        title: productNode.title,
        handle: productNode.handle,
        description: productNode.description || '',
        descriptionHtml: productNode.descriptionHtml || productNode.description || '',
        price: formatPrice(productNode.price?.minVariantPrice),
        priceAmount: parseFloat(productNode.price?.minVariantPrice?.amount || '0'),
        variants: productNode.variants?.edges?.map(({ node: variant }) => ({
            id: parseInt(variant.id.replace('gid://shopify/ProductVariant/', '')),
            title: variant.title,
            price: formatPrice(variant.price),
            priceAmount: parseFloat(variant.price?.amount || '0'),
            available: variant.availableForSale,
            options: variant.selectedOptions?.map(opt => opt.value) || [],
            option1: variant.selectedOptions?.[0]?.value || null,
            option2: variant.selectedOptions?.[1]?.value || null,
            option3: variant.selectedOptions?.[2]?.value || null,
            image: variant.image?.url || null,
            grid_image: variant.grid_image?.reference?.image?.url || null,
            featuredImage: variant.grid_image?.reference?.image?.url || variant.image?.url || null
        })) || []
    };
}

// Helper function to process and normalize product data
function processProduct(node) {
    const formatPrice = (priceObj) => {
        if (!priceObj?.amount) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: priceObj.currencyCode || 'USD'
        }).format(parseFloat(priceObj.amount));
    };

    if (node.customizableGoodsData?.reference?.active_in_production?.value !== 'true') {
      delete node.customizableGoodsData;
    }

    const sipModalData = node.sip_modal?.reference;
    
    // UPDATED: For customizable goods, prioritize grid_card_image from product_modal_group metafield
    let gridImage;
    if (node.customizableGoodsData?.reference?.grid_card_image?.reference?.image?.url) {
        gridImage = node.customizableGoodsData.reference.grid_card_image.reference.image.url;
    } else {
        gridImage = node.grid_image?.reference?.image?.url || node.featuredImage?.url;
    }
    
    const bundleBuilderImage = node.bundle_builder_img?.reference?.image?.url;

    // Process flavor profiles
    const flavorProfiles = [];
    if (sipModalData?.flavor_profile_icons?.references?.nodes && sipModalData?.flavor_profile_strings?.value) {
        const icons = sipModalData.flavor_profile_icons.references.nodes;
        const strings = JSON.parse(sipModalData.flavor_profile_strings.value || '[]');
        
        icons.forEach((icon, index) => {
            if (icon?.icon?.reference?.image?.url && strings[index]) {
                flavorProfiles.push({
                    image: icon.icon.reference.image.url.replace('https:', ''), // Convert to relative URL
                    text: strings[index]
                });
            }
        });
    }

    // Process modal images
    const modalImages = [];
    if (sipModalData?.modal_images?.references?.nodes) {
        sipModalData.modal_images.references.nodes.forEach(node => {
            if (node?.image?.url) {
                modalImages.push({
                    image: node.image.url.replace('https:', '') // Convert to relative URL
                });
            }
        });
    }

    // Helper function to convert Shopify date/time metafields to Unix timestamps
    const convertDateMetafieldToTimestamp = (metafield) => {
        if (!metafield?.value) return 0;
        
        // Handle ISO date string format from Shopify (e.g., "2025-05-02T13:00:00Z")
        if (typeof metafield.value === 'string' && metafield.type === 'date_time') {
            try {
                const date = new Date(metafield.value);
                return Math.floor(date.getTime() / 1000); // Convert to seconds
            } catch (error) {
                return 0;
            }
        }
        
        // If it's already a number (timestamp), return it
        if (!isNaN(metafield.value)) {
            return parseInt(metafield.value);
        }
        
        // Fallback: try to parse as date string
        try {
            const date = new Date(metafield.value);
            return Math.floor(date.getTime() / 1000); // Convert to seconds
        } catch (error) {
            return 0;
        }
    };

    // Process variants to match Liquid structure exactly
    const variants = node.variants.edges.map(({ node: variant }) => {
        // Process variant filtering tags
        const variantFilteringTags = [];
        if (variant.variant_filtering_tags?.references?.nodes) {
            variant.variant_filtering_tags.references.nodes.forEach(tag => {
                if (tag?.internal_name?.value && tag?.label?.value) {
                    variantFilteringTags.push({
                        id: tag.id,
                        internal_name: tag.internal_name.value,
                        label: tag.label.value,
                        category: tag.category?.reference ? {
                            id: tag.category.reference.id,
                            internal_name: tag.category.reference.internal_name?.value || '',
                            label: tag.category.reference.label?.value || ''
                        } : null
                    });
                }
            });
        }

        // UPDATED: Prioritize variant grid_image, then variant image, then product grid_image, then product featured image
        const variantImage = variant.grid_image?.reference?.image?.url || 
                           variant.image?.url || 
                           gridImage || 
                           '';

        return {
            id: parseInt(variant.id.replace('gid://shopify/ProductVariant/', '')), // Convert to number like Liquid
            title: variant.title,
            price: formatPrice(variant.price),
            available: variant.availableForSale,
            featuredImage: variantImage.replace('https:', ''), // Convert to relative URL
            grid_image: variant.grid_image?.reference?.image?.url || null, // ADD: Include variant grid_image
            launch_timestamp: convertDateMetafieldToTimestamp(variant.launch_timestamp) || convertDateMetafieldToTimestamp(node.launchTimestamp),
            options: variant.selectedOptions.map(opt => opt.value),
            option1: variant.selectedOptions[0]?.value || null,
            option2: variant.selectedOptions[1]?.value || null,
            option3: variant.selectedOptions[2]?.value || null,
            selectedOptions: variant.selectedOptions, // ADD: Keep GraphQL structure for ProductModal
            productType: node.subbrand_prefix?.value || '',
            sipData: variant.sip_modal?.reference ? variant.sip_modal.reference : null,
            variantFilteringTags
        };
    });

    // Process variant groups from options
    const variantGroups = [];
    // Note: GraphQL doesn't provide options_with_values like Liquid, so we'll build from variants
    if (node.variants?.edges?.length > 0) {
        const optionNames = new Set();
        const optionValues = {};
        
        node.variants.edges.forEach(({ node: variant }) => {
            variant.selectedOptions?.forEach(option => {
                optionNames.add(option.name);
                if (!optionValues[option.name]) {
                    optionValues[option.name] = new Set();
                }
                optionValues[option.name].add(option.value);
            });
        });
        
        optionNames.forEach(name => {
            variantGroups.push({
                name,
                values: Array.from(optionValues[name])
            });
        });
    }

    const productFilteringClass = node.product_filtering_class?.reference ? {
        id: node.product_filtering_class.reference.id,
        internal_name: node.product_filtering_class.reference.internal_name?.value || '',
        label: node.product_filtering_class.reference.label?.value || ''
    } : null;

    const productFilteringTags = [];
    if (node.product_filtering_tags?.references?.nodes) {
        node.product_filtering_tags.references.nodes.forEach(tag => {
            if (tag?.internal_name?.value && tag?.label?.value) {
                productFilteringTags.push({
                    id: tag.id,
                    internal_name: tag.internal_name.value,
                    label: tag.label.value,
                    category: tag.category?.reference ? {
                        id: tag.category.reference.id,
                        internal_name: tag.category.reference.internal_name?.value || '',
                        label: tag.category.reference.label?.value || ''
                    } : null
                });
            }
        });
    }

    // Determine if product is popular (you may want to move this logic to a config)
    const isPopular = ['Strawberry', 'Guava', 'Hibiscus', 'Sweet Tea'].includes(node.title?.trim());

    // Convert featured image URL to match Liquid format (relative URLs starting with //)
    let normalizedFeaturedImage = gridImage;
    if (normalizedFeaturedImage && normalizedFeaturedImage.startsWith('https://cdn.shopify.com/')) {
        normalizedFeaturedImage = normalizedFeaturedImage.replace('https:', '');
    }

    // Build sipData to match Liquid structure
    const sipData = sipModalData ? {
        display_name_admin_only: sipModalData.display_name_admin_only?.value || '',
        flavor_line_collection: sipModalData.flavor_line_collection?.reference?.handle || '',
        flavor_profile_icons: sipModalData.flavor_profile_icons?.references?.nodes?.map(icon => icon.id) || [],
        flavor_profile_strings: JSON.parse(sipModalData.flavor_profile_strings?.value || '[]'),
        is_flvr_lab: sipModalData.is_flvr_lab?.value === 'true',
        modal_images: sipModalData.modal_images?.references?.nodes?.map(img => img.id) || [],
        nutrition_image: sipModalData.nutrition_image?.reference?.id || '',
        override_sip_modal_background_desktop: sipModalData.override_sip_modal_background_desktop?.reference?.id || '',
        override_sip_modal_background_mobile: sipModalData.override_sip_modal_background_mobile?.reference?.id || '',
        title: sipModalData.title?.value || node.title
    } : null;

    // Process bundle components - for official Shopify bundles, extract from variants
    let bundleComponents = null;
    
    // Check if we have bundleComponents metafield first (custom bundles)
    if (node.bundleComponents?.reference) {
        bundleComponents = {
            id: node.bundleComponents.reference.id,
            fields: node.bundleComponents.reference.fields
        };
    } 
    // For official Shopify bundles, create bundle structure from variants
    else if (['Bundle', 'Standard Bundle', 'Bundle - Discountable'].includes(node.productType) && variants.length > 1) {
        // FIXED: Collect unique option values based on option NAMES, not index position
        const optionValuesByName = {};
        
        variants.forEach(variant => {
            // Extract from selectedOptions (GraphQL structure) using option names
            if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
                variant.selectedOptions.forEach((option) => {
                    const optionName = option.name.toLowerCase();
                    if (!optionValuesByName[optionName]) {
                        optionValuesByName[optionName] = new Set();
                    }
                    optionValuesByName[optionName].add(option.value);
                });
            }
        });
        
        // Create bundle components based on actual option names
        const componentMap = {};
        
        // Look for bottle-related options
        Object.entries(optionValuesByName).forEach(([optionName, values]) => {
            if (optionName.includes('bottle')) {
                componentMap['bottle'] = {
                    title: 'Bottle Color',
                    variants: Array.from(values).map(color => {
                        // Find ANY variant with this bottle color
                        const exampleVariant = variants.find(v => 
                            v.selectedOptions && v.selectedOptions.some(opt => 
                                opt.name.toLowerCase().includes('bottle') && opt.value === color
                            )
                        );
                        return {
                            id: exampleVariant.id,
                            title: color,
                            option1: color,
                            available: exampleVariant.available
                        };
                    })
                };
            }
        });
        
        // Look for lid-related options
        Object.entries(optionValuesByName).forEach(([optionName, values]) => {
            if (optionName.includes('lid')) {
                componentMap['lid'] = {
                    title: optionName.includes('lid') ? 'Lid Color' : 'Color',
                    variants: Array.from(values).map(color => {
                        // Find ANY variant with this lid color
                        const exampleVariant = variants.find(v => 
                            v.selectedOptions && v.selectedOptions.some(opt => 
                                opt.name.toLowerCase().includes('lid') && opt.value === color
                            )
                        );
                        return {
                            id: exampleVariant.id,
                            title: color,
                            option1: color,
                            available: exampleVariant.available
                        };
                    })
                };
            }
        });
        
        // If we only have one option and it's not specifically bottle or lid, treat it as the main option
        if (Object.keys(componentMap).length === 0 && Object.keys(optionValuesByName).length === 1) {
            const [optionName, values] = Object.entries(optionValuesByName)[0];
            componentMap['main'] = {
                title: optionName.charAt(0).toUpperCase() + optionName.slice(1), // Capitalize first letter
                variants: Array.from(values).map(color => {
                    const exampleVariant = variants.find(v => 
                        v.selectedOptions && v.selectedOptions.some(opt => opt.value === color)
                    );
                    return {
                        id: exampleVariant.id,
                        title: color,
                        option1: color,
                        available: exampleVariant.available
                    };
                })
            };
        }
        
        // Convert to array format
        bundleComponents = Object.values(componentMap).filter(comp => comp.variants.length > 0);
    }

    // Process customizable goods data
    const customizableGoodsData = node.customizableGoodsData?.reference ? {
        grid_card_title: node.customizableGoodsData.reference.grid_card_title?.value || null,
        grid_card_image: node.customizableGoodsData.reference.grid_card_image?.reference?.image?.url || null,
        dropdown_label: node.customizableGoodsData.reference.dropdown_label?.value || 'Size',
        default_member: node.customizableGoodsData.reference.default_member?.reference && Object.keys(node.customizableGoodsData.reference.default_member.reference).length > 0 ? {
            dropdown_option_label: node.customizableGoodsData.reference.default_member.reference.title || 'Default',
            product: processCustomizableProduct(node.customizableGoodsData.reference.default_member.reference)
        } : null,
        members: node.customizableGoodsData.reference.members?.references?.nodes?.map(member => ({
            id: member.id,
            dropdown_option_label: member.dropdown_option_label?.value || '',
            product: member.product?.reference ? processCustomizableProduct(member.product.reference) : null
        })).filter(member => member.product) || [],
        // ADDED: Calculate price range for the card display
        priceRange: (() => {
            const allProducts = [];
            
            // Add default member price
            if (node.customizableGoodsData.reference.default_member?.reference) {
                const defaultProduct = processCustomizableProduct(node.customizableGoodsData.reference.default_member.reference);
                if (defaultProduct) allProducts.push(defaultProduct);
            }
            
            // Add all member prices
            node.customizableGoodsData.reference.members?.references?.nodes?.forEach(member => {
                if (member.product?.reference) {
                    const memberProduct = processCustomizableProduct(member.product.reference);
                    if (memberProduct) allProducts.push(memberProduct);
                }
            });
            
            if (allProducts.length === 0) return null;
            
            const prices = allProducts.map(p => p.priceAmount).filter(p => p > 0);
            if (prices.length === 0) return null;
            
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            
            const formatPrice = (amount) => {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(amount);
            };
            
            return {
                min: minPrice,
                max: maxPrice,
                minFormatted: formatPrice(minPrice),
                maxFormatted: formatPrice(maxPrice),
                display: minPrice === maxPrice ? formatPrice(minPrice) : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
            };
        })()
    } : null;

    const processedProduct = {
        // Convert GID to simple number to match Liquid structure
        id: parseInt(node.id.replace('gid://shopify/Product/', '')),
        title: node.title || 'Untitled Product',
        subtitle: node.subtitle?.value || null,
        price: formatPrice(node.price?.minVariantPrice || { amount: '0', currencyCode: 'USD' }),
        compareAtPrice: node.compareAtPrice?.minVariantPrice ? formatPrice(node.compareAtPrice.minVariantPrice) : null,
        
        // FIX: Correct the field mapping
        type: node.productType || 'Unknown',  // This is the Shopify product type (Bundle - Discountable, etc.)
        productType: node.subbrand_prefix?.value || '',  // This is the subbrand prefix for display
        
        handle: node.handle || '',
        featuredImage: normalizedFeaturedImage || '',
        grid_image: gridImage || null, // UPDATED: Now includes customizable goods grid_card_image
        isOutOfStock: node.isOutOfStock?.value === 'true',
        available: node.available !== undefined ? node.available : true,
        newUntilTimestamp: convertDateMetafieldToTimestamp(node.newUntilTimestamp),
        launchTimestamp: convertDateMetafieldToTimestamp(node.launchTimestamp),
        description: node.descriptionHtml || node.description || '', // UPDATED: Prioritize HTML description
        tags: Array.isArray(node.tags) ? node.tags.join(', ') : (node.tags || ''), // Handle both string and array
        collections: node.collections?.edges ? node.collections.edges.map(({ node: coll }) => coll.title) : [],
        isPopular,
        
        // FIX: Add proper created_at field for sorting
        created_at: node.createdAt || new Date().toISOString(),
        createdAt: node.createdAt || new Date().toISOString(),
        
        flavorLine: {
            title: sipModalData?.flavor_line_collection?.reference?.title?.replace('Flavor Line: ', '') || '',
            handle: sipModalData?.flavor_line_collection?.reference?.handle || ''
        },
        modalImages,
        flavorLineBackgroundMobile: (sipModalData?.override_sip_modal_background_mobile?.reference?.image?.url || '').replace('https:', ''),
        flavorLineBackgroundDesktop: (sipModalData?.override_sip_modal_background_desktop?.reference?.image?.url || '').replace('https:', ''),
        flavorProfiles,
        nutritionImage: (sipModalData?.nutrition_image?.reference?.image?.url || '').replace('https:', ''),
        sipData,
        
        // NEW FILTERING FIELDS - ADD FINAL DEBUG
        productFilteringClass,
        productFilteringTags,
        
        maxQuantity: 99999,
        bundle_builder_image: bundleBuilderImage || null,
        variantGroups,
        variants,
        bundleComponents,
        customizableGoodsData // ENHANCED: Now includes all size variant data
    };

    return processedProduct;
}
